import React, { useState, useEffect } from 'react';

import { MapContainer, TileLayer, Marker, Popup, LayersControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet-color-markers';

import { FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';

import * as turf from '@turf/turf';

import API_BASE_URL from '../../../../Constants';

const RouteMapper = () => {
    const [buildings, setBuildings] = useState([]);
    const [routeVisibility, setRouteVisibility] = useState({});
    const [availableRoutes, setAvailableRoutes] = useState([]);
    const [loading, setLoading] = useState(false); // Added loading state
    const [routeColors, setRouteColors] = useState({});
    const [deviceCounts, setDeviceCounts] = useState({});
    const [selectAll, setSelectAll] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newRouteID, setNewRouteID] = useState('');
    const [selectedBuildings, setSelectedBuildings] = useState([]);
    const [currentLayer, setCurrentLayer] = useState(null);

    useEffect(() => {
        const fetchBuildings = async () => {
            try {
                setLoading(true); // Start loading
        
                // Fetch building data using API_BASE_URL
                const response = await fetch(`${API_BASE_URL}/api/setup/buildings/all`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include'
                });
        
                const buildingsData = await response.json();

                setBuildings(buildingsData);
        
                // Extract available routes
                const routes = [...new Set(buildingsData.map(b => b.routeID || 'Unassigned'))];
                console.log(routes);
                const visibility = {};
                routes.forEach(routeID => {
                    visibility[routeID] = true; // Initially, all routes are visible
                });
                setAvailableRoutes(routes);
                setRouteVisibility(visibility);
                setLoading(false); // End loading
            } catch (error) {
                console.error('Failed to fetch or geocode building data:', error);
                setLoading(false); // End loading even if there's an error
            }
        };        
        fetchBuildings();    
    }, []);

    useEffect(() => {
        const fetchDeviceCounts = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/setup/buildings/devices/count`, {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json',
                    },
                    credentials: 'include'
                });

                const data = await response.json();
                // console.log('Device Counts:', data);

                const counts = {};
                data.forEach(({buildingId, totalDevices}) => {
                    counts[Number(buildingId)] = totalDevices;
                });
                // console.log(counts)
                setDeviceCounts(counts);
                
            } catch (error){
                console.error('Failed to fetch device counts:', error);
            }
        };
        fetchDeviceCounts();
    }, []);


    // useEffect(() => {
    //     // Assign colors to new routes dynamically
    //     const assignColorsToRoutes = () => {
    //         const newRouteColors = { ...routeColors };
    
    //         availableRoutes.forEach(routeID => {
    //             if (!newRouteColors[routeID]) {
    //                 newRouteColors[routeID] = generateRandomColor();
    //             }
    //         });
            
    //         setRouteColors(newRouteColors);
    //     };
    
    //     assignColorsToRoutes();
    // }, [availableRoutes]); // Update colors when routes change

    useEffect(() => {
        const assignColorsToRoutes = () => {
            const newRouteColors = { ...routeColors };
    
            availableRoutes.forEach(routeID => {
                if (!newRouteColors[routeID]) {
                    newRouteColors[routeID] = getColorForRoute(routeID);
                }
            });
    
            setRouteColors(newRouteColors);
        };
    
        assignColorsToRoutes();
    }, [availableRoutes]); // Update colors when routes change

    const getPopupContent = (building) => {
        const addressParts = [
            building.addressLine1,
            building.addressLine2,
            building.city,
            building.state,
            building.zip,
        ].filter(Boolean);

        const address = addressParts.join(', ');
        const routeIdInfo = `Route: ${building.routeID || 'N/A'}`;
        const deviceCount = deviceCounts[Number(building.buildingID)] || 0;

        return (
            <>
                <div>{address}</div>
                <div>{routeIdInfo}</div>
                <div>{`Devices: ${deviceCount}`}</div>
            </>
        );
    };

    const handleDrawCreated = (e) => {
        const layer = e.layer;
        if (layer instanceof L.Polygon) {
            let polygonLatLngs = layer.getLatLngs()[0];
            console.log("Initial polygonLatLngs:", polygonLatLngs);

            // Ensure at least 3 unique points
            // if (polygonLatLngs.length < 3) {
            //     console.error("Polygon must have at least 3 points.");
            //     layer.remove();
            //     return;
            // }

            // Close the polygon by adding the first point if not already closed
            const firstPoint = polygonLatLngs[0];
            const lastPoint = polygonLatLngs[polygonLatLngs.length - 1];
            if (firstPoint.lat !== lastPoint.lat || firstPoint.lng !== lastPoint.lng) {
                polygonLatLngs = [...polygonLatLngs, firstPoint];
                console.log("Polygon closed, new polygonLatLngs:", polygonLatLngs);
            }

            // Verify we have at least 4 positions (3 unique + closing point)
            // if (polygonLatLngs.length < 4) {
            //     console.warn("Polygon has fewer than 4 positions, but should be valid with 3 points + closing.");
            // }

            // Create a valid Turf polygon
            try {
                const coordinates = polygonLatLngs.map(latlng => [latlng.lng, latlng.lat]);
                console.log("Coordinates for Turf polygon:", coordinates);
                const turfPolygon = turf.polygon([coordinates]);

                const selectedBuildings = buildings.filter(building => {
                    if (
                        building.latitude === null ||
                        building.longitude === null ||
                        typeof building.latitude !== 'number' ||
                        typeof building.longitude !== 'number' ||
                        building.buildingStatus !== 'Active'
                    ) return false;
                    const turfPoint = turf.point([building.longitude, building.latitude]);
                    return turf.booleanPointInPolygon(turfPoint, turfPolygon);
                });

                console.log("Selected buildings:", selectedBuildings.length);

                if (selectedBuildings.length > 0) {
                    setSelectedBuildings(selectedBuildings);
                    setCurrentLayer(layer);
                    setIsModalOpen(true);
                } else {
                    layer.remove();
                }
            } catch (error) {
                console.error("Error creating Turf polygon:", error);
                layer.remove(); 
            }
        } else {
            console.error("The drawn shape is not a polygon.");
        }
    };

    const handleModalSubmit = async () => {
        if (!newRouteID || !currentLayer || selectedBuildings.length === 0) return;

        const updatedBuildings = selectedBuildings.map(building => ({ ...building, routeID: newRouteID }));
        for (const building of updatedBuildings) {
            try {
                await fetch(`${API_BASE_URL}/api/setup/buildings/${building.buildingID}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ ...building, routeID: newRouteID }),
                });
            } catch (error) {
                console.error(`Error updating building ID: ${building.buildingID}`, error);
            }
        }

        const updatedBuildingsState = buildings.map(building =>
            selectedBuildings.some(selected => selected.buildingID === building.buildingID)
                ? { ...building, routeID: newRouteID }
                : building
        );
        setBuildings(updatedBuildingsState);
        const updatedRoutes = [...new Set(updatedBuildingsState.map(b => b.routeID || 'Unassigned'))];
        setAvailableRoutes(updatedRoutes);
        setRouteVisibility(prevState => ({ ...prevState, [newRouteID]: true }));
        currentLayer.remove();
        setIsModalOpen(false);
        setNewRouteID('');
        setSelectedBuildings([]);
    };
    

    
    // const generateRandomColor = () => {
    //     const letters = '0123456789ABCDEF';
    //     let color = '#';
    //     for (let i = 0; i < 6; i++) {
    //         color += letters[Math.floor(Math.random() * 16)];
    //     }
    //     return color;
    // };

    const standardColors = [
        '#FF5733', '#33FF57', '#3357FF', '#FF33A1', '#A133FF', '#33FFF5', '#FF8C33', '#8CFF33', '#338CFF', '#FF338C'
    ];
    
    const hashStringToColor = (str) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        const color = `#${((hash >> 24) & 0xFF).toString(16).padStart(2, '0')}${((hash >> 16) & 0xFF).toString(16).padStart(2, '0')}${((hash >> 8) & 0xFF).toString(16).padStart(2, '0')}`;
        return color;
    };
    
    const getColorForRoute = (routeID) => {
        const index = availableRoutes.indexOf(routeID);
        if (index < standardColors.length) {
            return standardColors[index];
        }
        return hashStringToColor(routeID);
    };

    const createCustomIcon = (color) => {
        const svgIcon = `
            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
                <path fill="${color}" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 6.9 11.8 26.3 12.2 27L12.5 41l.3-1.5C13.2 38.8 25 19.4 25 12.5 25 5.6 19.4 0 12.5 0z"/>
                <circle cx="12.5" cy="12.5" r="5.5" fill="#FFF"/>
            </svg>
        `;
        return new L.DivIcon({
            html: svgIcon,
            className: 'custom-div-icon',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
        });
    };
    
    const getRouteDetails = () => {
        const details = {};

        buildings.forEach(building => {
            if (building.buildingStatus === 'Active') {
                const routeID = building.routeID || 'Unassigned';
                details[routeID] = details[routeID] || { totalBuildings: 0, totalDevices: 0 };

                // Increment building count
                details[routeID].totalBuildings += 1;

                // Increment device count
                const deviceCount = deviceCounts[Number(building.buildingID)] || 0;
                details[routeID].totalDevices += deviceCount;
            }
        });
        return details;
    };


    const handleRouteVisibilityChange = (routeID) => {
        setRouteVisibility(prevState => ({
            ...prevState,
            [routeID]: !prevState[routeID],
        }));
    };

    const handleSelectAll = () => {
        const newVisibility = {};
        availableRoutes.forEach(routeID => {
            newVisibility[routeID] = true;
        });
        setRouteVisibility(newVisibility);
    };

    const handleDeselectAll = () => {
        const newVisibility = {};
        availableRoutes.forEach(routeID => {
            newVisibility[routeID] = false;
        });
        setRouteVisibility(newVisibility);
    };

    const handleSelectAllChange = () => {
        setSelectAll(!selectAll);
        if(!selectAll){
            handleSelectAll();
        } else {
            handleDeselectAll();
        }
    }


    const createMap = () => {
        if (buildings.length === 0) return null;
    
        const center = [40.7831, -73.9712];
    
        const validBuildings = buildings.filter(b => 
            b.latitude && 
            b.longitude &&
            b.buildingStatus === 'Active' // Filter out inactive buildings
        );
    
        return (
            <MapContainer center={center} zoom={12} style={{ height: '100%', width: '100%' }}>
                <LayersControl position="topright">
                    <LayersControl.BaseLayer checked name="Default">
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; OpenStreetMap contributors'
                        />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="Topographical">
                        <TileLayer
                            url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                            attribution='Map data: &copy; OpenStreetMap contributors, SRTM | Map style: &copy; OpenTopoMap (CC-BY-SA)'
                        />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="Satellite">
                        <TileLayer
                            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                            attribution='Tiles &copy; Esri &mdash; Source: Esri, USGS, NOAA'
                        />
                    </LayersControl.BaseLayer>
                </LayersControl>
                <FeatureGroup>
                    <EditControl
                        position="topright"
                        onCreated={handleDrawCreated}
                        draw={{
                            rectangle: false,
                            polygon: true,
                            marker: false,
                            polyline: false,
                            circlemarker: false,
                            circle: false,
                        }}
                    />
                </FeatureGroup>
                {validBuildings.map((building) => {
                    //const routeID = building.routeID;
                    const routeID = building.routeID || 'Unassigned';
                    const isVisible = routeVisibility[routeID];
                    if (!isVisible) return null; // Do not render if route is not visible

                    // Use the assigned color for the route
                    const markerColor = routeColors[routeID] || '#000000'; // Default to black if color is not found

                    // Create a custom icon with the color
                    const customIcon = createCustomIcon(markerColor);

    
                    return (
                        <Marker
                            key={building.buildingID}
                            position={[building.latitude, building.longitude]}
                            icon={customIcon}
                        >
                            <Popup>{getPopupContent(building)}</Popup>
                        </Marker>
                    );
                })}
            </MapContainer>
        );
    };
    
    const generateMapHTML = () => {
        // Serialize data for the map
        const serializedBuildings = JSON.stringify(
            buildings.filter(b => b.latitude && b.longitude && b.buildingStatus === 'Active')
                .map(b => ({
                    latitude: b.latitude,
                    longitude: b.longitude,
                    routeID: b.routeID || 'Unassigned',
                    popupContent: getPopupContent(b).props.children
                        .map(child => child.props ? child.props.children : child)
                        .join('<br>')
                        .replace(/'/g, "\\'") 
                        .replace(/"/g, '\\"') 
                }))
        );
        const serializedRouteColors = JSON.stringify(routeColors);
    
        // Serialize data for the Route List
        const routeDetails = getRouteDetails();
        const serializedRouteDetails = JSON.stringify(routeDetails);
        const serializedAvailableRoutes = JSON.stringify(availableRoutes);
        const serializedRouteVisibility = JSON.stringify(routeVisibility);
        const serializedSelectAll = JSON.stringify(selectAll);
        const sortedRoutes = [...availableRoutes].sort((a, b) => a === 'Unassigned' ? -1 : b === 'Unassigned' ? 1 : a.localeCompare(b));
    
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>New Map</title>
                <link rel="stylesheet" href="https://unpkg.com/leaflet/dist/leaflet.css" />
                <style>
                    body { 
                        margin: 0; 
                        padding: 0; 
                        display: flex; 
                        height: 100vh; 
                        overflow: hidden; 
                    }
                    #map { 
                        flex: 3; 
                        height: 100%; 
                    }
                    #legend { 
                        flex: 1; 
                        min-width: 250px; 
                        max-width: 325px; 
                        height: 100%; 
                        overflow: hidden; 
                        border-left: 1px solid #ccc; 
                        background-color: #f9f9f9; 
                        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1); 
                        padding: 10px; 
                        font-family: Arial, sans-serif; 
                        display: flex; 
                        flex-direction: column; 
                    }
                    #legend-header { 
                        position: sticky; 
                        top: 0; 
                        background-color: #f9f9f9; 
                        z-index: 10; 
                        padding-bottom: 10px; 
                        border-bottom: 2px solid #ccc; 
                    }
                    #legend-content { 
                        flex: 1; 
                        overflow-y: auto; 
                        overflow-x: hidden; 
                        -webkit-overflow-scrolling: touch; 
                    }
                    .route-item { 
                        display: flex; 
                        justify-content: space-between; 
                        align-items: center; 
                        padding: 8px 0; 
                        border-bottom: 1px solid #ccc; 
                    }
                    .route-color { 
                        display: inline-block; 
                        width: 15px; 
                        height: 15px; 
                        border-radius: 50%; 
                        margin-right: 10px; 
                    }
                </style>
            </head>
            <body>
                <div id="map"></div>
                <div id="legend">
                    <div id="legend-header">
                        <h3 style="margin-bottom: 2px; font-size: 18px; color: #333; text-align: center; font-weight: bold;">Routes List</h3>
                        <div style="display: flex; justify-content: space-between; font-weight: bold; padding: 10px 0; text-align: center;">
                            <span style="flex: 2; text-align: left;">
                                <input type="checkbox" id="select-all" checked="${selectAll}" onchange="toggleSelectAll()" style="margin-right: 8px;">Route
                            </span>
                            <span style="flex: 1; text-align: center;">Buildings</span>
                            <span style="flex: 1; text-align: center;">Devices</span>
                        </div>
                    </div>
                    <div id="legend-content">
                        ${sortedRoutes.map(routeID => {
                            const { totalBuildings = 0, totalDevices = 0 } = routeDetails[routeID] || {};
                            return `
                                <div class="route-item">
                                    <div style="display: flex; align-items: center; flex: 2;">
                                        <input type="checkbox" id="checkbox-${routeID}" checked="${routeVisibility[routeID]}" onchange="toggleRouteVisibility('${routeID}')">
                                        <span class="route-color" style="background-color: ${routeColors[routeID] || '#000000'};"></span>
                                        <span style="font-size: 14px; font-weight: ${routeID === 'Unassigned' ? 'bold' : 'normal'}">${routeID === 'Unassigned' ? 'Unassigned' : routeID}</span>
                                    </div>
                                    <span style="flex: 1; text-align: center; font-size: 14px;">${totalBuildings}</span>
                                    <span style="flex: 1; text-align: center; font-size: 14px;">${totalDevices}</span>
                                </div>
                            `;
                        }).join('')}
                    </div>
                </div>
                <script src="https://unpkg.com/leaflet/dist/leaflet.js"></script>
                <script>
                    // Data from the parent window
                    const buildings = ${serializedBuildings};
                    const routeColors = ${serializedRouteColors};
                    const routeDetails = ${serializedRouteDetails};
                    const availableRoutes = ${serializedAvailableRoutes};
                    let routeVisibility = ${serializedRouteVisibility};
                    let selectAll = ${serializedSelectAll};
    
                    // Initialize the map
                    var map = L.map('map').setView([40.7831, -73.9712], 12);
                    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                        attribution: '© OpenStreetMap contributors'
                    }).addTo(map);
    
                    // Store markers for visibility toggling
                    const markers = {};
                    buildings.forEach(b => {
                        const markerColor = routeColors[b.routeID] || '#000000';
                        const svgIcon = \`<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41"><path fill="\${markerColor}" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 6.9 11.8 26.3 12.2 27L12.5 41l.3-1.5C13.2 38.8 25 19.4 25 12.5 25 5.6 19.4 0 12.5 0z"/><circle cx="12.5" cy="12.5" r="5.5" fill="#FFF"/></svg>\`;
                        const marker = L.marker([b.latitude, b.longitude], {
                            icon: L.divIcon({
                                html: svgIcon,
                                className: 'custom-div-icon',
                                iconSize: [25, 41],
                                iconAnchor: [12, 41],
                                popupAnchor: [1, -34]
                            })
                        }).bindPopup(b.popupContent);
                        markers[b.routeID] = markers[b.routeID] || [];
                        markers[b.routeID].push(marker);
                        if (routeVisibility[b.routeID]) marker.addTo(map);
                    });
    
                    // Function to toggle individual route visibility
                    function toggleRouteVisibility(routeID) {
                        routeVisibility[routeID] = !routeVisibility[routeID];
                        const routeMarkers = markers[routeID] || [];
                        if (routeVisibility[routeID]) {
                            routeMarkers.forEach(marker => marker.addTo(map));
                        } else {
                            routeMarkers.forEach(marker => map.removeLayer(marker));
                        }
                        document.getElementById('checkbox-' + routeID).checked = routeVisibility[routeID];
                        // Update selectAll state if all routes are selected/deselected
                        selectAll = availableRoutes.every(route => routeVisibility[route]);
                        document.getElementById('select-all').checked = selectAll;
                    }
    
                    // Function to toggle all routes
                    function toggleSelectAll() {
                        selectAll = !selectAll;
                        document.getElementById('select-all').checked = selectAll;
                        availableRoutes.forEach(routeID => {
                            routeVisibility[routeID] = selectAll;
                            const routeMarkers = markers[routeID] || [];
                            if (selectAll) {
                                routeMarkers.forEach(marker => marker.addTo(map));
                            } else {
                                routeMarkers.forEach(marker => map.removeLayer(marker));
                            }
                            document.getElementById('checkbox-' + routeID).checked = selectAll;
                        });
                    }
                </script>
            </body>
            </html>
        `;
    };

    // Function to open a new map in a separate window
    const openNewMap = () => {
        const newMapWindow = window.open('', '_blank', 'width=800,height=600');
        if (newMapWindow) {
            newMapWindow.document.write(generateMapHTML());
            newMapWindow.document.close();
        } else {
            alert('Please allow popups for this site to open a new map.');
        }
    };

    

    const renderLegend = () => {

        // Get route details (Total Building and Total Devices)
        const routeDetails = getRouteDetails();
        
        const sortedRoutes = [...availableRoutes].sort((a, b) => {
            if (a === 'Unassigned') return -1; // Ensure 'Unassigned' comes first
            if (b === 'Unassigned') return 1;
            return a.localeCompare(b);
        });
        

        return (
            <div style={{
                padding: '10px',
                backgroundColor: '#f9f9f9',
                border: '1px solid #ccc',
                borderRadius: '8px',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                fontFamily: 'Arial, sans-serif',
                height: '100%', // Ensure it takes full height of parent
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Fixed Header */}
                <div style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#f9f9f9', // Match background to avoid overlap issues
                    zIndex: 10, // Ensure it stays above scrolling content
                    paddingBottom: '10px',
                    borderBottom: '2px solid #ccc',
                }}>
                    <h3 style={{
                        marginBottom: '2px',
                        fontSize: '18px',
                        color: '#333',
                        textAlign: 'center',
                        fontWeight: 'bold',
                    }}>Routes List</h3>
                    
                    {/* Legend Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontWeight: 'bold',
                        padding: '10px 0',
                        textAlign: 'center',
                    }}>
                        <span style={{ flex: 2, textAlign: 'left' }}>
                            <input
                                type="checkbox"
                                checked={selectAll}
                                onChange={handleSelectAllChange}
                                style={{ marginRight: '8px' }}
                            />
                            Route
                        </span>
                        <span style={{ flex: 1, textAlign: 'center' }}>Buildings</span>
                        <span style={{ flex: 1, textAlign: 'center' }}>Devices</span>
                    </div>
                </div>
    
                {/* Scrollable Content */}
                <div style={{
                    flex: 1, // Take remaining space
                    overflowY: 'auto', // Enable vertical scrolling
                }}>
                    {sortedRoutes.map((routeID) => {
                        const { totalBuildings = 0, totalDevices = 0 } = routeDetails[routeID] || {};
                        return (
                            <div key={routeID} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px 0',
                                borderBottom: '1px solid #ccc',
                            }}>
                                {/* Route Details */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flex: 2,
                                }}>
                                    <input
                                        type="checkbox"
                                        style={{ marginRight: '8px' }}
                                        checked={routeVisibility[routeID]}
                                        onChange={() => handleRouteVisibilityChange(routeID)}
                                    />
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            width: '15px',
                                            height: '15px',
                                            backgroundColor: routeColors[routeID] || '#000000',
                                            borderRadius: '50%',
                                            marginRight: '10px',
                                        }}
                                    ></span>
                                    <span style={{ fontSize: '14px', fontWeight: routeID === 'Unassigned' ? 'bold' : 'normal' }}>
                                        {routeID === 'Unassigned' ? 'Unassigned' : `${routeID}`}
                                    </span>
                                </div>
                                {/* Building and Device Counts */}
                                <span style={{ flex: 1, textAlign: 'center', fontSize: '14px' }}>{totalBuildings}</span>
                                <span style={{ flex: 1, textAlign: 'center', fontSize: '14px' }}>{totalDevices}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };
    
    const renderModal = () => (
        isModalOpen && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
            }}>
                <div style={{
                    backgroundColor: '#fff',
                    padding: '20px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    width: '300px',
                    textAlign: 'center',
                }}>
                    <h3>Assign Route ID</h3>
                    <p>Enter a new or existing Route ID for {selectedBuildings.length} selected buildings:</p>
                    <input
                        type="text"
                        value={newRouteID}
                        onChange={(e) => setNewRouteID(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px',
                            margin: '10px 0',
                            border: '1px solid #000000',
                            borderRadius: '4px',
                            outline: 'none',
                        }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <button
                            onClick={() => {
                                setIsModalOpen(false);
                                setNewRouteID('');
                                currentLayer.remove();
                                setSelectedBuildings([]);
                            }}
                            style={{ padding: '8px 16px', backgroundColor: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleModalSubmit}
                            style={{ padding: '8px 16px', backgroundColor: '#007BFF', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                            Submit
                        </button>
                    </div>
                </div>
            </div>
        )
    );
    
    return (
        <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {loading ? (
                <div className="loading-indicator">
                    <p>Loading data, please wait...</p>
                </div>
            ) : (
                <>
                    <div style={{ flex: 3, overflow: 'hidden', position: 'relative', zIndex: 1 }}>
                        <button 
                            onClick={openNewMap}
                            style={{
                                position: 'absolute',
                                top: '10px',
                                left: '45px',
                                zIndex: 1000,
                                padding: '8px 16px',
                                backgroundColor: '#007BFF',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Open New Map
                        </button>
                        {createMap()}
                    </div>
                    <div style={{ 
                        flex: 1,
                        minWidth: '250px',
                        maxWidth: '325px',
                        overflow: 'hidden', // Prevent outer overflow
                        borderLeft: '1px solid #ccc',
                        backgroundColor: '#f9f9f9',
                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)', 
                        maxHeight: 'calc(100vh - 20px)',
                    }}>
                        {renderLegend()}
                    </div>
                    {renderModal()}
                </>
            )}
        </div>
    );
};

export default RouteMapper;